// src/contexts/AuthContext.tsx frissítése
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import authService, {
  ProfilePictureResponse,
  UserProfile,
} from "../services/auth.service";
import {
  User,

  CustomerRegisterData,
  ShopRegisterData,
} from "../types/index";

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  registerCustomer: (userData: CustomerRegisterData) => Promise<boolean>;
  registerShop: (userData: ShopRegisterData) => Promise<boolean>;
  updateUser: (updatedUser: User) => void;
  // Új funkciók a profilkép kezeléséhez
  uploadProfilePicture: (
    userId: string,
    imageFile: File
  ) => Promise<ProfilePictureResponse | null>;
  getUserProfile: (userId: string) => Promise<UserProfile | null>;
}

// Context létrehozása
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider komponens
export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Token és felhasználó betöltése indításkor

  useEffect(() => {
    const loadStoredAuth = async () => {
      console.log(
        "[AuthContext.useEffect.loadStoredAuth] Attempting to load stored auth data..."
      );
      setIsLoading(true); // Induláskor mindig true
      try {
        const storedToken = authService.getToken(); // Ez már logol
        const storedUser = authService.getUser(); // Ez már logol

        console.log(
          "[AuthContext.useEffect.loadStoredAuth] Checking token validity for storedToken:",
          storedToken
        );
        const isTokenActuallyValid = authService.isTokenValid(); // Ez már logolja a részleteket

        if (storedToken && storedUser && isTokenActuallyValid) {
          console.log(
            "[AuthContext.useEffect.loadStoredAuth] Stored auth data found AND VALID. Setting context state. User:",
            storedUser
          );
          setToken(storedToken);
          setUser(storedUser);
          // Az authService.setAuthHeader(storedToken) itt redundáns lehet, ha az interceptor jól működik minden kérésnél.
          // De ha a default headert is akarjuk állítani az app indulásakor:
          authService.setAuthHeader(storedToken);
        } else {
          console.warn(
            "[AuthContext.useEffect.loadStoredAuth] No valid stored auth data found or token invalid. Logging out. StoredToken:",
            storedToken,
            "StoredUser:",
            storedUser,
            "IsTokenValid:",
            isTokenActuallyValid
          );
          // Csak akkor hívjunk logoutot, ha tényleg volt mit törölni, vagy ha explicit ki akarunk jelentkeztetni
          if (storedToken || storedUser) {
            authService.logout(); // Ez törli a localStorage-t és a default headert
          }
          setToken(null);
          setUser(null);
        }
      } catch (error) {
        console.error(
          "[AuthContext.useEffect.loadStoredAuth] Error loading stored auth data:",
          error
        );
        authService.logout();
        setToken(null);
        setUser(null);
      } finally {
        console.log(
          "[AuthContext.useEffect.loadStoredAuth] Finished loading. Setting isLoading to false."
        );
        setIsLoading(false);
      }
    };
    loadStoredAuth();
  }, []); // Csak mountkor

  // Bejelentkezés
  const login = async (email: string, password: string): Promise<boolean> => {
    console.log("[AuthContext.login] Attempting login with email:", email);
    setIsLoading(true); // Jelezzük a login folyamatát
    try {
      const authResponse = await authService.login(email, password); // authService.login már logol
      if (authResponse && authResponse.user && authResponse.token) {
        console.log(
          "[AuthContext.login] Login successful via authService. User data received:",
          authResponse.user
        );
        setUser(authResponse.user);
        setToken(authResponse.token);
        // Az authService.login már elvileg beállította a default headert és mentett localStorage-ba
        setIsLoading(false);
        return true;
      } else {
        console.warn(
          "[AuthContext.login] Login failed - authService.login returned invalid response.",
          authResponse
        );
        logout(); // Ez törli a tokent és nullázza a state-et
        setIsLoading(false);
        return false;
      }
    } catch (error) {
      console.error("[AuthContext.login] Login error in AuthContext:", error);
      logout();
      setIsLoading(false);
      return false;
    }
  };

  // Kijelentkezés
  const logout = () => {
    console.log("[AuthContext.logout] Logging out user from AuthContext.");
    authService.logout(); // Ez törli a localStorage-t és a default headert
    setToken(null);
    setUser(null);
    
  };

  // Vásárló regisztráció
  const registerCustomer = async (
    userData: CustomerRegisterData
  ): Promise<boolean> => {
    return authService.registerCustomer(userData);
  };

  // Bolt regisztráció
  const registerShop = async (userData: ShopRegisterData): Promise<boolean> => {
    return authService.registerShop(userData);
  };

  // Felhasználó adatainak frissítése
  const updateUser = (updatedUserData: Partial<User>) => {
    // Partial<User>
    if (!user) return;
    const mergedUser = { ...user, ...updatedUserData };
    setUser(mergedUser);
    authService.setUser(mergedUser);
    console.log("AuthContext: User data updated.", mergedUser);
  };

  // Új: Profilkép feltöltése
  const uploadProfilePicture = async (
    userId: string,
    imageFile: File
  ): Promise<ProfilePictureResponse | null> => {
    try {
      const formData = new FormData();
      formData.append("profileImage", imageFile);

      const response = await authService.uploadProfilePicture(userId, formData);

      if (response && response.success && user) {
        updateUser({ profileImage: response.imageUrl });
        console.log("AuthContext: Profile picture updated successfully.");
      }

      return response;
    } catch (error) {
      console.error("Profile picture upload error:", error);
      return null;
    }
  };

  // Új: Felhasználói profil lekérése
  const getUserProfile = async (
    userId: string
  ): Promise<UserProfile | null> => {
    return authService.getUserProfile(userId);
  };

  // Context értékeinek összeállítása - Most már az isLoading is benne van
  const value: AuthContextType = {
    // Explicit típust adunk neki
    user,
    token,
    isAuthenticated: !!token && !!user,
    isLoading, // <<<--- ITT ADJUK ÁT
    login,
    logout,
    registerCustomer,
    registerShop,
    updateUser,
    uploadProfilePicture,
    getUserProfile,
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        Betöltés...
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook a Context használatához
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
